import { Fragment, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { StudioIcon } from './StudioIcon';
import { useAccountLocale } from '../../i18n';
import { StudioHome } from './StudioHome';
import { StudioCardsSkeleton, StudioSegmentsSkeleton, StudioStatus, StudioSegments } from './StudioBrowseControls';
import { StudioZero, type StudioZeroAction } from './StudioZero';
import { useResultsTransition } from '../../ui/use-results-transition';
import { pop, reduced } from '../../ui/motion';
import { type BrowseState, formats, casts } from './studio-browse-state';
import { accountLocale, uiFormat, uiIn, uiText } from '../../i18n';
import { studioApi, type StudioDocument, type StudioPage, type StudioQuery } from './studio-api';
import { cover, displayTitle, documentKind, needText, priceParts, priceUnit, record, rows, text, title } from './studio-content';
import { useStudioList } from './use-studio-list';
import { StudioCards } from './StudioCards';
import { useStudioDocument } from './use-studio-document';
import { Sticky } from '../../ui/Sticky';
import { StudioToolRows } from './StudioToolRows';
import { useStudioArtwork } from './use-studio-artwork';
import { studioOwner } from './studio-store';
import { studioHomeMemory } from './studio-home-memory';
import { FadeImg } from '../../ui/LateFade';
import { FilterSheet } from '../../ui/FilterSheet';
import { FILTER_ICON, FILTER_SUB, type FilterGroup } from '../../ui/filter-sheet';

const isHomeView = (state: BrowseState) => state.view === 'home';
/** Содержимое на месте заглушек проявляется один раз (.ld-in: только opacity, без сдвига); класс снимается по окончании. Скрытый блок
    (поверх выдача поиска) встаёт без проявления */
function ldIn(el: HTMLElement | null) {
  if (!el || el.hidden) return;
  el.classList.add('ld-in');
  const end = (e: AnimationEvent) => {
    if (e.target !== el) return;
    el.removeEventListener('animationend', end);
    el.classList.remove('ld-in');
  };
  el.addEventListener('animationend', end);
}
/** Сколько после самозакрытия поля шапки нажатие «‹» считается закрытием поиска, а не уходом со страницы (qBack эталона), мс */
const SEARCH_AUTO_MS = 400;
/** Подписи форматов шаблона, как FMT прототипа: строка выдачи поиска начинается с них («Reels · 2 человека») */
const formatLabels: Record<string, string> = {
  reels: 'Reels',
  post: 'Пост',
  photo: 'Фотосессия',
  video: 'Видео',
  circle: 'Кружок',
  avatar: 'Аватар',
  slides: 'Слайды',
};
/** Формат в родительном падеже для пустой сцены подборки (FGEN прототипа) */
const formatGenitive: Record<string, string> = { reels: 'Reels', photo: 'фото', video: 'видео', circle: 'кружков' };
/** Состав словами (KCAST прототипа) */
const castNames: Record<string, string> = { one: 'Один', two: 'Двое', many: 'Несколько', kid: 'Дети', pet: 'Питомцы' };
/** Запасные названия разделов: сервер отдаёт их в `sections` ответа /app/studio/tools, это — если поля нет */
const fallbackSections: Record<string, string> = {
  all: 'Все',
  circle: 'Кружки',
  photo: 'Фото',
  video: 'Видео',
  voice: 'Голос и музыка',
  text: 'Текст',
  biz: 'Для бизнеса',
};
/** Поиск прототипа (engine.ts): слова запроса, «ё» = «е» */
const normalize = (value: string) => value.toLocaleLowerCase().replaceAll('ё', 'е');
const queryWords = (value: string) => normalize(value).split(/[\s,.;:!?«»"()]+/).filter(Boolean);
/** Очки совпадения (qScore прототипа): -1 — не подходит; 3 — название начинается с запроса;
    2 — слово названия начинается с каждого слова; 1 — каждое слово в названии; 0 — по остальному тексту */
function queryScore(name: string, hay: string, words: string[]): number {
  if (!words.every(word => hay.includes(word))) return -1;
  const lower = normalize(name),
    parts = lower.split(/[\s,.()«»-]+/);
  return lower.startsWith(words.join(' '))
    ? 3
    : words.every(word => parts.some(part => part.startsWith(word)))
      ? 2
      : words.every(word => lower.includes(word))
        ? 1
        : 0;
}
/** Ключ цены для «Дешевле» (priceKey прототипа): за секунду считается как за 3 с, без цены — в конец */
function priceKey(item: StudioDocument): number {
  const fields = record(item.price);
  const amount = typeof item.price === 'number' ? item.price : Number(fields.amount ?? fields.circle);
  if (!Number.isFinite(amount)) return Infinity;
  return amount * (priceUnit(fields) === 'per_second' ? 3 : 1);
}
const position = (item: StudioDocument) => Number(item.position ?? 0);
export function StudioBrowse({
  sortOpenAtStart = false,
  heroPaused = false,
  state,
  onChange,
  onOpen,
  onAction,
  onView,
  onReady,
  onBack,
}: {
  /** [ТЕСТ] меню ⇅ раскрыто сразу: состояние из хэша (afm, ksortm, tsortm) */
  sortOpenAtStart?: boolean;
  /** Поверх просмотра лист шаблона или экран создания: слайдер главной не листается сам */
  heroPaused?: boolean;
  state: BrowseState;
  /** fit — поправка фильтров по ответу новой вкладки: лента к началу уже ушла сменой вкладки, второй раз не прокручиваем */
  onChange: (change: Partial<BrowseState>, options?: { fit?: boolean }) => void;
  onOpen: (item: StudioDocument) => void;
  onAction: (action: unknown) => void;
  onView: (view: BrowseState['view']) => void;
  onReady?: (state: BrowseState) => void;
  onBack?: () => void;
}) {
  const [sortOpen, setSortOpen] = useState(sortOpenAtStart);
  const searchInput = useRef<HTMLInputElement>(null);
  const ghosts = useRef<HTMLDivElement>(null);
  const loadMore = useRef<HTMLDivElement>(null);
  const ask = useRef<HTMLHeadingElement>(null);
  const searchBox = useRef<HTMLDivElement>(null);
  const homeBody = useRef<HTMLDivElement>(null);
  const subBox = useRef<HTMLDivElement>(null);
  /** Прокрутка главной до поиска (S.hqTop эталона): её возвращает очистка поля. Помнится и между показами раздела
      (studioHomeMemory, как S модуля эталона): поиск главной — часть вида раздела, возврат встаёт на его выдаче */
  const searchTop = useRef<number | null>(state.view === 'home' && state.q.trim() ? (studioHomeMemory.hqTop ?? null) : null);
  const keepSearchTop = (value: number | null) => {
    searchTop.current = value;
    studioHomeMemory.hqTop = value;
  };
  const searchId = useId();
  const searchShown = !isHomeView(state) && (state.searchOpen || !!state.q);
  /* Фокус ставится только при раскрытии поля на месте: у эталона поле, раскрытое с самого начала
     (вход по хэшу), фокуса не получает — иначе :focus-visible скругляет поле, чего в эталоне нет. */
  const focusOnOpen = useRef(false);
  useEffect(() => {
    if (searchShown && focusOnOpen.current) searchInput.current?.focus({ preventScroll: true });
    focusOnOpen.current = true;
  }, [searchShown]);
  /** Когда поле шапки закрылось само (qAuto эталона) */
  const searchAutoClosed = useRef(0);
  /* Поле шапки потеряло фокус пустым (нажали в ленту, убрали клавиатуру) — закрывается само; с текстом остаётся, окно
     ушло в фон — тоже остаётся (focusout эталона: решение в следующей задаче, когда фокус уже встал на новое место) */
  const searchBlur = (input: HTMLInputElement) => {
    setTimeout(() => {
      const field = input.closest('.st-qf'),
        active = document.activeElement;
      if (!input.isConnected || !field?.closest('.st-tlh.q') || input.value.trim() || !document.hasFocus()) return;
      if (active && active !== document.body && field.contains(active)) return;
      searchAutoClosed.current = Date.now();
      onChange({ searchOpen: false, q: '' });
    }, 0);
  };
  const menuKey = `${state.view}:${state.collection?.id ?? ''}`;
  const [sortOwner, setSortOwner] = useState(menuKey);
  if (sortOwner !== menuKey) {
    setSortOwner(menuKey);
    setSortOpen(false);
  }
  const locale = useAccountLocale() === 'ru' ? 'ru' : 'en';
  const artwork = useStudioArtwork(locale);
  /* Документы раздела переживают уход из «Студии» (studio-store, у своего владельца): возврат рисуется из них сразу,
     как mount эталона из S, а ответы сервера подтверждают их в фоне */
  const home = useStudioDocument(signal => studioApi.home(signal), 'home', { remember: true });
  const appSummary = useStudioDocument(signal => studioApi.list('apps', { limit: 1 }, signal), 'studio-app-count', { remember: true });
  const catalog = useStudioDocument(signal => studioApi.catalog(signal), 'catalog', { remember: true });
  const tools = useStudioDocument(signal => studioApi.allTools(signal), 'tools', { remember: true });
  /* Главная после заглушек (StudioHomeWait) проявляется один раз (.ld-in); из памяти раздела она встаёт сразу, без заглушек и проявления */
  const homeWait = !home.data && !home.error,
    homeSk = useRef(false);
  useLayoutEffect(() => {
    if (homeWait) {
      homeSk.current = true;
      return;
    }
    if (!homeSk.current) return;
    homeSk.current = false;
    if (home.data) ldIn(homeBody.current);
  }, [homeWait, home.data]);
  const collectionId = state.collection?.id;
  /* Документ страницы подборки приходит вместе с первой страницей её выдачи — одним шагом, как openColl эталона рисует
     страницу сразу: шапка со строками, лента и сцена встают одним commit (companion списка). Документ зависит только от
     подборки и вкладки верхней строки (top входит в запрос, §6.5): смена формата, состава, порядка и поиска берёт его из
     памяти этого показа страницы. Новый показ страницы запрашивает его заново */
  const collectionDocs = useRef(new Map<string, unknown>());
  useEffect(() => {
    collectionDocs.current.clear();
  }, [menuKey]);
  const loadCollection = (asked: StudioQuery, signal: AbortSignal) => {
    const id = collectionId,
      top = asked.top ?? 'all',
      key = `${studioOwner()}:${id}:${top}`,
      kept = collectionDocs.current.get(key);
    if (kept !== undefined || !id) return Promise.resolve(kept ?? null);
    return studioApi.collection(id, top, signal).then(document => {
      collectionDocs.current.set(key, document);
      return document;
    });
  };
  const searching = state.view === 'home' && !!state.q.trim();
  const query: StudioQuery = {
    q: state.q.trim() || undefined,
    /* Порядок по умолчанию — дело сервера: свой набор он объявляет в available_sorts и на любой
       другой отвечает 409. Передаём только то, что человек выбрал в меню ⇅, — меню и собрано
       из available_sorts, поэтому такой порядок эндпоинт заведомо принимает. */
    sort: state.sortPicked ? state.sort : undefined,
    format: state.format === 'all' ? undefined : state.format,
    cast: state.cast === 'all' ? undefined : state.cast,
    tab: state.tab === 'all' || state.tab === 'colls' ? undefined : state.tab,
    collection: state.view === 'collection' ? text(state.collection?.code) || String(collectionId) : undefined,
    top: state.view === 'collection' && state.top !== 'all' ? state.top : undefined,
  };
  /* Вкладка «Подборки» ищет у себя, а не общим поиском: эталон (collsFeed) отбирает подборки
     из своего же списка по названию, чипу и описанию, поэтому и запрос остаётся прежним. */
  const collsTab = state.view === 'catalog' ? state.tab === 'colls' : state.top === 'colls';
  const kind = collsTab && state.view === 'catalog' ? 'app-collections' : searching ? 'search' : 'apps';
  const showList = searching || state.view === 'catalog' || state.view === 'collection';
  const patch = (change: Partial<BrowseState>) => onChange(change);
  /* Вид второй строки вкладки каталога говорит сама вкладка — поле tabs в ответе GET /app/studio/catalog
     (то же, что у подборки, docs/03-api.md §6.2, §6.4): 'cast' — состав, иначе форматы; у «Все» и «Рекомендуем» —
     форматы. undefined — сервер вида не прислал: по cast_counts его не угадываем, сервер может слать их у любой вкладки */
  const tabCast = (id: string): boolean | undefined => {
    if (id === 'all' || id === 'rec') return false;
    const tab = rows(catalog.data?.tabs).find(item => text(item.id) === id);
    return tab && 'tabs' in tab ? tab.tabs === 'cast' : undefined;
  };
  /* Смена вкладки каталога оставляет формат и состав, если такой пункт есть в новой второй строке (subFit эталона):
     пункт чужой строки становится «Все» сразу, без лишнего запроса. Вид строки не известен — оба «Все»,
     скрытого фильтра под чужой строкой не бывает */
  const tabFit = (tab: string): Partial<BrowseState> => {
    const cast = tab === 'colls' ? undefined : tabCast(tab);
    return cast === true ? { format: 'all' } : cast === false ? { cast: 'all' } : { format: 'all', cast: 'all' };
  };
  /* Неактивный пункт новой строки (счётчик ноль) говорит только ответ вкладки: тогда выбор становится «Все», а сам
     ответ не показывается — придёт выдача уже без него. Только первая выдача новой вкладки, как subFit эталона:
     выбор на той же вкладке не трогаем, у пустого формата своя сцена. Счётчики — у вкладки, а не у поиска (§6.2) */
  function fit(page: StudioPage, asked: StudioQuery) {
    // поздний ответ не правит фильтры, которые уже сменились
    if (state.view !== 'catalog' || asked.q || JSON.stringify(asked) !== JSON.stringify(query)) return false;
    if (!list.shownQuery || shownTab === state.tab) return false;
    /* формат и состав — списки через запятую: пункты без работ уходят из выбора, не осталось ни одного — «Все» */
    const kept = (counts: unknown, ids?: string) => {
      if (!ids || !counts || typeof counts !== 'object') return null;
      const all = ids.split(','), left = all.filter(id => Number(record(counts)[id]) > 0);
      return left.length === all.length ? null : left.length ? left.join(',') : 'all';
    };
    const change: Partial<BrowseState> = {};
    const format = kept(page.format_counts, asked.format), cast = kept(page.cast_counts, asked.cast);
    if (format) change.format = format;
    if (cast) change.cast = cast;
    if (!Object.keys(change).length) return false;
    onChange(change, { fit: true });
    return true;
  }
  /* Прежняя выдача держится на экране, пока грузится новая, — только в пределах одной страницы. Выдачи помнятся
     и после ухода из раздела (remember): возврат встаёт с них сразу */
  const isCollection = state.view === 'collection';
  const list = useStudioList(kind, query, showList, {
    scope: `${state.view}:${collectionId ?? ''}`,
    fit,
    remember: true,
    companion: isCollection ? loadCollection : undefined,
  });
  /* Документ подборки — той выдачи, что на экране: пока грузится другая вкладка верхней строки, шапка держит прежний
     (обе строки остаются на месте), и новая вторая строка встаёт вместе с новой лентой (seg2Swap эталона) */
  const collectionDoc = isCollection ? (list.companion as StudioDocument | null | undefined) ?? undefined : undefined;
  /* Выдача поиска главной встаёт, когда пришла первая; до того на месте остаётся главная (у эталона выдача
     синхронная, пустого кадра между ними нет) */
  const results = searching && list.ready;
  /* На экране — выдача, пришедшая последней: пока грузится новая, раскладка (сетка «Подборок» или кладка)
     и вторая строка остаются её и сменяются вместе с лентой одной сменой выдачи (seg2Swap эталона) */
  const shownColls = list.shownQuery
    ? state.view === 'catalog'
      ? list.shownKind === 'app-collections'
      : list.shownQuery.top === 'colls'
    : collsTab;
  const quoted = (value: string) => `«${value.trim()}»`;
  /* Разделы страницы «Все инструменты» приходят в ответе /app/studio/tools полем sections
     ([{id, title, word}], docs/03-api.md §6.7) — названием раздела подписан сегмент, им же
     ищется инструмент (toolHay прототипа берёт название, а не ключ). */
  const sectionList = rows(tools.data?.sections).filter(item => text(item.id));
  const sectionItems: string[][] = sectionList.length
    ? sectionList.map(item => [text(item.id), title(item) || fallbackSections[text(item.id)] || text(item.id)])
    : [['all', fallbackSections.all], ...[...new Set((tools.data?.items ?? []).map(item => text(item.section)).filter(Boolean))].map(id => [id, fallbackSections[id] ?? id])];
  const sectionName = (id: string) => sectionItems.find(([key]) => key === id)?.[1] ?? fallbackSections[id] ?? id;
  /* Пустая выдача: свой заголовок, пояснение и одно действие на каждый случай — как в прототипе
     (srZeroHTML, tlZeroHTML, catLabelHTML, kZeroHTML). Разметку сцены рисует StudioZero. */
  function zeroScene(): { title: string; text?: string; action?: StudioZeroAction } {
    /* запрос — той выдачи, что на экране: пока грузится новая, сцена говорит о прежней */
    const query = (list.shownQuery?.q ?? state.q).trim();
    const section = state.view === 'tools' && state.section !== 'all' ? sectionName(state.section) : '';
    if (searching)
      return {
        title: 'Ничего не нашлось',
        text: uiFormat('По запросу {0} нет ни инструментов, ни шаблонов. Попробуйте другое слово или посмотрите все инструменты', quoted(query)),
        action: { label: 'Все инструменты', onClick: () => onView('tools') },
      };
    if (state.view === 'tools')
      return section
        ? {
            title: 'Ничего не нашлось',
            text: uiFormat('По запросу {0} в разделе {1} ничего нет. Поищите во всех инструментах', quoted(query), quoted(section)),
            action: { label: 'Искать во всех', onClick: () => patch({ section: 'all' }) },
          }
        : {
            title: 'Ничего не нашлось',
            text: uiFormat('По запросу {0} инструментов нет. Попробуйте другое слово', quoted(query)),
            action: { label: 'Очистить поиск', icon: 'close', onClick: () => patch({ q: '', searchOpen: false }) },
          };
    if (state.view === 'collection') {
      const name = title(collectionDoc ?? state.collection ?? {});
      /* Страница-хозяин («Вы в кино») говорит про вкладку и в сценах, а не про подборку и шаблоны
         (kZeroHTML эталона): «По запросу «танец» в «Трендах» ничего нет. Поищите во всех сценах».
         Назад она уводит на вкладку «Все» этой же страницы, а не в каталог. */
      const hostTabs = rows(record(collectionDoc).top_tabs);
      if (query && hostTabs.length && state.top !== 'all') {
        const tab = hostTabs.find(item => text(item.id) === state.top);
        const where =
          state.top === 'trend'
            ? uiText('в «Трендах»')
            : state.top === 'rec'
              ? uiText('в «Рекомендуем»')
              : uiFormat('в подборке {0}', quoted(title(tab ?? {})));
        return {
          title: 'Ничего не нашлось',
          text: uiFormat('По запросу {0} {1} ничего нет. Поищите во всех сценах', quoted(query), where),
          action: { label: 'Искать во всех сценах', onClick: () => patch({ top: 'all', cast: 'all' }) },
        };
      }
      if (query)
        return {
          title: 'Ничего не нашлось',
          text: uiFormat('В подборке {0} ничего по запросу {1}. Поищите во всех шаблонах', quoted(name), quoted(query)),
          action: { label: 'Искать во всех шаблонах', onClick: () => onView('catalog') },
        };
      const data = record(collectionDoc);
      /* Простая подборка (kZeroHTML): «В подборке «Пасха» нет видео» — формат в родительном
         падеже (FGEN) или состав словами; у страницы-хозяина свой текст (ниже). */
      if (!rows(data.top_tabs).length) {
        const what =
          data.tabs === 'cast'
            ? uiFormat('сцен {0}', state.cast.split(',').map(id => quoted(uiText(castNames[id] ?? id))).join(', '))
            : /* формат во множественном числе: ключ «формат: …», как у прототипа (fmtGen в engine.ts) */
              state.format.split(',').map(id => uiIn('формат', formatGenitive[id] ?? formatLabels[id] ?? id)).join(', ');
        return {
          title: uiFormat('В подборке {0} нет {1}', quoted(name), what),
          text: uiText('В этой подборке пока нет таких шаблонов. Посмотрите её целиком или выберите другую вкладку'),
          action: { label: 'Вся подборка', onClick: () => patch({ q: '', format: 'all', cast: 'all' }) },
        };
      }
      return {
        title: uiFormat('В подборке {0} нет таких шаблонов', quoted(name)),
        text: uiText('Посмотрите её целиком или выберите другую вкладку'),
        action: { label: 'Вся подборка', onClick: () => patch({ q: '', format: 'all', cast: 'all' }) },
      };
    }
    if (query)
      return state.format !== 'all'
        ? {
            title: 'Ничего не нашлось',
            text: uiFormat('По запросу {0} в этом формате ничего нет. Поищите во всех форматах', quoted(query)),
            action: { label: 'Искать во всех форматах', onClick: () => patch({ format: 'all' }) },
          }
        : {
            title: 'Ничего не нашлось',
            text: uiFormat('По запросу {0} среди шаблонов ничего нет. Попробуйте другое слово', quoted(query)),
            action: { label: 'Очистить поиск', icon: 'close', onClick: () => patch({ q: '', searchOpen: false }) },
          };
    return state.format !== 'all'
      ? {
          title: 'Здесь нет шаблонов',
          text: uiText('Посмотрите во всех форматах'),
          action: { label: 'Все форматы', onClick: () => patch({ format: 'all' }) },
        }
      : { title: 'Пока ничего нет', text: uiText('Шаблоны этого формата появятся совсем скоро') };
  }
  const isHome = state.view === 'home';
  const name = isHome
    ? 'Что хотите сделать?'
    : state.view === 'catalog'
      ? 'Все шаблоны'
      : state.view === 'tools'
        ? 'Все инструменты'
        : title(collectionDoc ?? state.collection ?? {});
  /* Строки страницы «Все инструменты»: отбор и порядок — как tlGroups прототипа.
     Строки только для поиска входят лишь при запросе. */
  const words = queryWords(state.q);
  const toolHay = (item: StudioDocument) =>
    normalize(
      [
        title(item),
        text(record(item.need).text) || text(item.need),
        sectionName(text(item.section)),
        ...(Array.isArray(item.aliases) ? item.aliases : []),
        ...(Array.isArray(item.keywords) ? item.keywords : []),
      ].join(' '),
    );
  /* Лента вкладки «Подборки»: при запросе — отбор по названию, чипу и описанию (collsFeed/itemHay
     эталона: norm(c.n + ' ' + chip + ' ' + c.d + ' подборка')), витрины особой подборки при поиске нет. */
  const collHay = (item: StudioDocument) =>
    normalize([title(item), text(item.chip), text(item.description), 'подборка', uiText('подборка')].join(' '));
  const cardItems =
    shownColls && words.length
      ? list.items
          .map(item => ({ item, score: queryScore(title(item), collHay(item), words) }))
          .filter(row => row.score >= 0)
          .sort((a, b) => b.score - a.score)
          /* найденная особая подборка — обычная стопка: флаг hero сервер мог оставить */
          .map(row => (row.item.hero ? { ...row.item, hero: false } : row.item))
      : list.items;
  const toolItems = (tools.data?.items ?? [])
    .filter(item => (words.length || !item.search_only) && (state.section === 'all' || item.section === state.section))
    .map(item => ({ item, score: words.length ? queryScore(title(item), toolHay(item), words) : 0 }))
    .filter(row => row.score >= 0)
    .sort((a, b) =>
      state.sort === 'name'
        ? title(a.item).localeCompare(title(b.item), accountLocale())
        : state.sort === 'name_desc'
          ? title(b.item).localeCompare(title(a.item), accountLocale())
          : state.sort === 'cheap'
          ? priceKey(a.item) - priceKey(b.item) || position(a.item) - position(b.item)
          : state.sort === 'new'
            ? Number(text(b.item.badge) === 'new') - Number(text(a.item.badge) === 'new') || position(a.item) - position(b.item)
            : /* «Популярные»: сначала по очкам совпадения, затем по месту в наборе (tlGroups) */
              b.score - a.score || position(a.item) - position(b.item),
    )
    .map(row => row.item);
  const collectionData = record(collectionDoc);
  /* Вкладка выдачи, что на экране: пока грузится другая вкладка каталога, вторая строка и отметка в ней — её */
  const shownTab = list.shownQuery ? (list.shownKind === 'app-collections' ? 'colls' : (list.shownQuery.tab ?? 'all')) : state.tab;
  /* Вторая строка — состав: у подборки это её поле tabs, у вкладки каталога — поле tabs вкладки (tabCast);
     сервер его не прислал — как прежде, по cast_counts ответа (docs/03-api.md §6.2) */
  const castRow = isCollection ? collectionData.tabs === 'cast' : (tabCast(shownTab) ?? !!list.cast_counts);
  /* Верхняя строка: у каталога — вкладки каталога, у страницы-хозяина — её top_tabs (§6.5) */
  const topTabs = isCollection ? rows(collectionData.top_tabs) : state.view === 'catalog' ? rows(catalog.data?.tabs) : [];
  /* Нижняя строка: сервер отдаёт tabs_list с отметкой disabled; пустой список — строки нет.
     На вкладке «Подборки» строки не бывает, контейнер встаёт одной пилюлей. */
  // Пустой tabs_list — это «строки нет» (активных пунктов кроме «Все» меньше двух), а не отсутствие данных:
  // запасной список берём, только когда поля в ответе вовсе нет.
  const hasServerTabs = isCollection && Array.isArray(collectionData.tabs_list);
  const serverTabs = hasServerTabs ? rows(collectionData.tabs_list) : [];
  const serverDisabled = hasServerTabs
    ? new Set(serverTabs.filter(item => item.disabled === true).map(item => text(item.id)))
    : undefined;
  const hidesSubRow = shownColls;
  const subRow = hidesSubRow
    ? []
    : hasServerTabs
      ? serverTabs
      : isCollection
        ? /* у подборки строку даёт только сервер: пока ответа нет, строки нет — иначе подписи
             запасного списка успевают встать в разметку и бегунок сегмента остаётся на их ширине */
          []
        : // во второй строке у «Все» подпись «Все форматы» (subRow прототипа); у состава — просто «Все»
          (castRow ? casts : formats).map(([id, label]) => ({ id, label: !castRow && id === 'all' ? 'Все форматы' : label }));
  /* Отметка второй строки. Пока грузится другая вкладка каталога или верхней строки страницы-хозяина, на экране её
     прежняя строка — и отмечен в ней прежний пункт, а не поправленный по новой вкладке (у эталона старая строка гаснет
     со своей отметкой) */
  const rowStale =
    !!list.shownQuery &&
    (state.view === 'catalog' ? shownTab !== state.tab : isCollection && (list.shownQuery.top ?? 'all') !== state.top);
  const subValue =
    rowStale && list.shownQuery
      ? castRow
        ? (list.shownQuery.cast ?? 'all')
        : (list.shownQuery.format ?? 'all')
      : castRow
        ? state.cast
        : state.format;
  /** Состав сохраняется при смене вкладки, если он есть в новой строке, иначе «Все» (kFit прототипа) */
  const keepCast = (nextTop: string) => (nextTop === 'colls' || state.cast === 'all' ? 'all' : state.cast);
  /* Золото — не больше чем у одной вкладки, правило применяет сервер (§6.2) */
  const goldTabs = new Set(topTabs.filter(item => item.accent === 'gold').map(item => text(item.id)));
  /* Состав второй строки с неактивными пунктами (subSig эталона): сменился — новая строка входит общей анимацией */
  const subCounts = serverDisabled ? undefined : castRow ? list.cast_counts : list.format_counts;
  const subSig = subRow
    .map(item => {
      const id = text(item.id);
      const off = id !== 'all' && (serverDisabled ? serverDisabled.has(id) : subCounts !== undefined && (subCounts[id] ?? 0) === 0);
      return off ? `${id}-` : id;
    })
    .join(',');
  const subWas = useRef({ page: menuKey, sig: subSig, ready: list.ready });
  useLayoutEffect(() => {
    const was = subWas.current;
    subWas.current = { page: menuKey, sig: subSig, ready: list.ready };
    // первая выдача страницы строку не перерисовывает — она проявляется только при смене на месте
    if (was.page === menuKey && was.ready && was.sig !== subSig && subSig && !reduced()) pop(subBox.current);
  }, [menuKey, subSig, list.ready]);
  /* Выдача на экране: строки поиска главной, строки «Всех инструментов», плитки каталога и подборки */
  const feed = state.view === 'tools' ? toolItems : state.view === 'home' ? (results ? list.items : []) : cardItems;
  const feedKeys = feed.map(item => `${documentKind(item)}:${item.id}`).join(',');
  /* Те же два набора, что у эталона: строки и подзаголовки выдачи (.st-tr, .st-trh) и плитки лент (.st-app, .st-stk).
     Смена страницы или переход главная ↔ выдача поиска — не смена выдачи: там своё проявление. Первая выдача страницы —
     тоже: страница встаёт с ней целиком (у эталона лента рисуется вместе со страницей, без поочерёдного проявления плиток) */
  const transition = useResultsTransition({
    ghosts,
    selector: '.st-tr,.st-trh,.st-app,.st-stk',
    signature: feedKeys,
    scope: `${state.view}:${collectionId ?? ''}:${results ? 'sr' : ''}:${list.ready ? 'on' : ''}`,
    label: '.st-lbl,.st-sre',
  });
  /* Сбой выдачи: первая страница не пришла — на месте ленты сцена ошибки с повтором (в держателе пустой сцены, вместо пустой
     страницы); не пришла следующая — строка догрузки говорит об этом и предлагает повтор, сама больше не догружает */
  const listFailed = list.failed && !list.items.length;
  const moreFailed = list.failed && !!list.items.length && !list.loading;
  const failure = <StudioStatus error retry={list.reload} />;
  const canLoadMore = !!list.next && !list.loading && !list.failed && state.view !== 'tools';
  /* Догрузка: строка «Загружаем ещё» подходит к видимой части — показываем следующую страницу.
     Наблюдатель смотрит внутри прокручиваемой колонки раздела, с запасом 120 px, как в прототипе. */
  const more = useRef(list.more);
  useEffect(() => {
    more.current = list.more;
  });
  useEffect(() => {
    const line = loadMore.current;
    const scroller = line?.closest('.pg');
    if (!line || !scroller || !canLoadMore) return;
    // Строка подошла к нижнему краю колонки — показываем следующую страницу (catMore прототипа)
    const check = () => {
      if (line.getBoundingClientRect().top < scroller.getBoundingClientRect().bottom + 120) more.current();
    };
    check();
    scroller.addEventListener('scroll', check, { passive: true });
    // Наблюдатель — основной путь; прокрутка страхует его там, где он молчит (скрытая вкладка, короткая лента)
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(entries => entries.some(entry => entry.isIntersecting) && more.current(), { root: scroller, rootMargin: '120px' });
    observer?.observe(line);
    return () => {
      scroller.removeEventListener('scroll', check);
      observer?.disconnect();
    };
  }, [canLoadMore]);
  /* Набор «Сначала» страницы «Все инструменты» приходит в ответе /app/studio/tools полем sorts */
  const availableSorts =
    state.view === 'tools'
      ? Array.isArray(tools.data?.sorts)
        ? /* «От Я до А» инструментов считает клиент: есть «От А до Я» — есть и она */
          (tools.data.sorts as unknown[]).filter((item): item is string => typeof item === 'string').flatMap(item => (item === 'name' ? ['name', 'name_desc'] : [item]))
        : ['pop', 'new', 'cheap', 'name', 'name_desc']
      : Array.isArray(list.available_sorts)
        ? list.available_sorts
        : ['new', 'name', 'cheap'];
  /* Порядок по умолчанию: у каталога и «Всех инструментов» он общий («pop», как C.sort и T.sort
     эталона), а у страницы подборки свой — `kDef=c=>c&&c.sort==='id'?'new':'pop'` (engine.ts:1108).
     Сервер ставит его первым в available_sorts. От него же зависит заливка кнопки ⇅
     (`aSortOn=()=>C.sort!=='pop'` и `sortOn:s!==kDef(c)`). */
  const defaultSort = state.view === 'collection' ? (availableSorts[0] ?? 'pop') : 'pop';
  /* Порядок на экране: выбранный человеком, иначе умолчание страницы (K.sort эталона null — kSort берёт kDef). Умолчание
     подборки известно из её первой страницы; до неё ⇅ не залита — заливка ставится один раз, а не мигает до ответа */
  const sortValue = state.sortPicked ? state.sort : defaultSort;
  const sortOn = !!state.sortPicked && state.sort !== defaultSort && (state.view !== 'collection' || Array.isArray(list.available_sorts));
  /* Каталог и страница-хозяин (владелец 30.10.2026, как «Озвучка»): шапка — одна строка вкладок, лупа и ⇅; вторая строка (формат или состав:
     сколько людей, с питомцем) ушла в меню ⇅ отдельной группой. ⇅ залита и тогда, когда в этой группе выбрано не «Все» */
  const subInMenu = (state.view === 'catalog' || (isCollection && !!topTabs.length)) && !!subRow.length;
  /* Лист ⇅ (ui/filter-sheet.ts, владелец 30.10.2026): порядок — один, формат или состав — сразу несколько (список через запятую в запросе,
     docs/03-api.md §6.2); пустой выбор — «Все». ⇅ залита, когда что-то отличается от умолчаний */
  const filterGroups: FilterGroup[] = [
    {
      key: 'sort',
      label: uiText('Сначала'),
      items: (
        [
          ['pop', 'Популярные'],
          ['new', 'Новые'],
          ['cheap', 'Дешевле'],
          ['short', 'Короче'],
          ['name', 'От А до Я'],
          ['name_desc', 'От Я до А'],
        ] as const
      )
        .filter(([value]) => availableSorts.includes(value))
        .map(([id, label]) => ({ id, label: uiText(label), sub: uiText(FILTER_SUB[id] ?? '') })),
      value: [sortValue],
      def: [defaultSort],
    },
    /* «Формат» и «Состав» — обе группы, где у вкладки есть и то и другое (владелец 30.10.2026: «Двое + Reels»). Группа прежней второй строки
       берёт её пункты (у страницы-хозяина — tabs_list сервера), другая — по счётчикам ответа; группа без единого шаблона не показывается */
    ...(['format', 'cast'] as const).flatMap(kind => {
      const own = subInMenu && castRow === (kind === 'cast');
      const counts = kind === 'cast' ? list.cast_counts : list.format_counts;
      if (hidesSubRow || !(state.view === 'catalog' || isCollection) || (!own && !counts)) return [];
      const state_ = kind === 'cast' ? state.cast : state.format;
      const value = (own ? subValue : state_) === 'all' ? [] : (own ? subValue : state_).split(',');
      const items = (own ? subRow.map(item => [text(item.id), text(item.label)]) : kind === 'cast' ? casts : formats)
        .filter(([id]) => id !== 'all')
        .map(([id, label]) => ({
          id,
          label: uiText(label),
          sub: FILTER_SUB[id] ? uiText(FILTER_SUB[id]) : undefined,
          icon: FILTER_ICON[id],
          disabled: own && serverDisabled ? serverDisabled.has(id) : counts !== undefined && (Number(counts[id]) || 0) === 0,
        }));
      if (!value.length && !items.some(item => !item.disabled)) return [];
      return [{ key: kind, label: uiText(kind === 'cast' ? 'Состав' : 'Формат'), multi: true, items, value }];
    }),
  ];
  const filterChange = (key: string, value: string[]) => {
    if (key === 'sort') patch({ sort: value[0] as BrowseState['sort'], sortPicked: true });
    else {
      const list = value.length ? value.join(',') : 'all';
      patch(key === 'cast' ? { cast: list } : { format: list });
    }
  };
  useEffect(() => {
    if (
      (state.view === 'home' && !searching && home.data) ||
      (state.view === 'tools' && tools.data) ||
      (showList && !list.loading && !list.error)
    )
      onReady?.(state);
  }, [state, searching, home.data, tools.data, showList, list.loading, list.error, onReady]);
  /* Пустая сцена — у пришедшей выдачи: пока грузится новая, прежняя сцена остаётся на месте; у сбоя своя сцена, и на повторе тоже */
  const zeroReady = list.ready && !list.items.length && !list.failed;
  /* Главная ↔ выдача поиска (hqPaint эталона): входящий блок проявляется (pop). Вход в выдачу — hqPin: страница была
     прокручена ниже поля — выдача не ниже окна, прокрутка встаёт на низ заголовка-вопроса, поле стоит закреплённым,
     выдача сразу под ним. Очистка возвращает главную на прежнее место — в том же кадре, до отрисовки */
  const homeMode = state.view === 'home' ? (results ? 'sr' : 'body') : '';
  const modeWas = useRef(homeMode);
  useLayoutEffect(() => {
    const was = modeWas.current;
    modeWas.current = homeMode;
    const box = searchBox.current,
      body = homeBody.current,
      column = box?.closest<HTMLElement>('.pg');
    if (!was || !homeMode || was === homeMode || !box || !body || !column) return;
    if (homeMode === 'sr') {
      const top = searchTop.current,
        heading = ask.current;
      if (heading && top !== null) {
        const y = Math.round(column.scrollTop + heading.getBoundingClientRect().bottom - column.getBoundingClientRect().top);
        if (top > y) {
          box.style.minHeight = `${column.clientHeight}px`;
          column.scrollTop = y;
        }
      }
    } else {
      box.style.minHeight = '';
      if (searchTop.current !== null) column.scrollTop = searchTop.current;
      keepSearchTop(null);
    }
    if (!reduced()) pop(homeMode === 'sr' ? box : body);
  }, [homeMode]);
  /* Возврат в раздел сразу на выдаче поиска главной (вид из памяти): выдача снова держит закреплённое место hqPin, если до поиска
     главная была прокручена ниже поля — короткая выдача не отпускает прокрутку, которую раздел ставит следом */
  useLayoutEffect(() => {
    const box = searchBox.current,
      heading = ask.current,
      column = box?.closest<HTMLElement>('.pg'),
      top = searchTop.current;
    if (homeMode !== 'sr' || !box || !heading || !column || top === null) return;
    const y = Math.round(column.scrollTop + heading.getBoundingClientRect().bottom - column.getBoundingClientRect().top);
    if (top > y) box.style.minHeight = `${column.clientHeight}px`;
    // только первый показ: дальше место держит смена вида главной (эффект выше)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /** Слой копий ушедших карточек: у каждого экрана он свой и стоит последним (st-ghosts прототипа) */
  const ghostLayer = <div className="st-ghosts" ref={ghosts} aria-hidden="true" />;
  /** Строка выдачи поиска: подборка и шаблон идут строкой с обложкой (collRowHTML, appRowHTML) */
  const searchRow = (item: StudioDocument) => {
    const kind = documentKind(item);
    const picture = cover(item);
    const price = priceParts(item.price);
    const subtitle =
      kind === 'collection'
        ? [uiText('Подборка'), needText(item)].filter(Boolean).join(' · ')
        : [uiText(formatLabels[text(item.format)] ?? text(item.format)), needText(item)].filter(Boolean).join(' · ');
    return (
      <button
        key={`${kind}:${item.id}`}
        type="button"
        className="st-mr st-tr"
        data-k={`${kind === 'collection' ? 'c' : 'a'}:${item.id}`}
        {...(kind === 'collection' ? { 'data-coll': String(item.id) } : { 'data-app': String(item.id), 'aria-haspopup': 'dialog' as const })}
        onClick={() => onOpen(item)}
      >
        <span className="ic th">{picture && <FadeImg src={picture} alt="" />}</span>
        <span className="m">
          <b>
            <span className="tt">{displayTitle(item)}</span>
          </b>
          <span>{subtitle}</span>
        </span>
        {!!price.text && (
          <span className="st-pr">
            {price.text}
            {!!price.unit && <span className="u">{price.unit}</span>}
          </span>
        )}
        <span className="rt">
          <StudioIcon kind="chev" />
        </span>
      </button>
    );
  };
  /* Выдача поиска главной группами «Инструменты · Подборки · Шаблоны» (srHTML): подзаголовок
     .st-h.st-trh с числом и группа строк .st-grp — плоско внутри .st-srl, без своих обёрток */
  const searchGroups = ([
    ['t', 'Инструменты', 'tool'],
    ['c', 'Подборки', 'collection'],
    ['a', 'Шаблоны', 'app'],
  ] as const).map(([key, label, group]) => ({
    key,
    label,
    group,
    items: list.items.filter(item => documentKind(item) === group || (group === 'app' && documentKind(item) === 'reel_template')),
  }));
  return (
    <>
      {isHome && (
        <h1 ref={ask} className="st-ask">
          {uiText(name)}
        </h1>
      )}
      <Sticky
        flush={!isHome}
        /* bc-sticky и flush ставит сам Sticky; .q раскрывает поле поверх названия (bot-core.css) */
        className={isHome ? 'st-hq' : `st-tlh${searchShown ? ' q' : ''}`}
      >
        {isHome ? (
          /* Поле поиска главной — .search прямым ребёнком .st-hq (hqHTML прототипа) */
          <div className="search">
            <StudioIcon kind="search" />
            <input
              type="search"
              maxLength={100}
              value={state.q}
              onChange={event => {
                const value = event.target.value;
                // место главной — до первой буквы: выдача встанет позже, когда придёт
                if (!state.q.trim() && value.trim()) keepSearchTop(ask.current?.closest('.pg')?.scrollTop ?? null);
                patch({ q: value });
              }}
              placeholder={uiText('Найти инструмент или шаблон')}
              aria-label={uiText('Найти инструмент или шаблон')}
            />
            {/* крестик стоит в разметке всегда, пустое поле — hidden (BotCore.html.search) */}
            <button type="button" className="sx" hidden={!state.q} onClick={() => patch({ q: '' })} aria-label={uiText('Очистить поиск')}>
              <StudioIcon kind="close" />
            </button>
          </div>
        ) : (
          <div className="st-tlr">
            {/* Шапка-каталог держит все части в разметке, видимость переключает класс .q у .st-tlh
                (bot-core.css, phead): поле выезжает поверх названия, лупа уходит масштабом. */}
            <button
              type="button"
              className="iconb xbtn"
              data-studio-back
              /* «‹», которое и забрало фокус у пустого поля, закрыло поиск — страницу оно не закрывает (qBack эталона) */
              onClick={() => Date.now() - searchAutoClosed.current >= SEARCH_AUTO_MS && onBack?.()}
              aria-label={uiText('Назад')}
            >
              <StudioIcon kind="back" />
            </button>
            <h2>{uiText(name)}</h2>
            <div className="search st-qf">
              <StudioIcon kind="search" />
              <input
                ref={searchInput}
                id={searchId}
                type="search"
                maxLength={100}
                value={state.q}
                tabIndex={searchShown ? 0 : -1}
                onChange={event => patch({ q: event.target.value })}
                onBlur={event => searchBlur(event.currentTarget)}
                onKeyDown={event => {
                  // Enter убирает клавиатуру; поле с текстом остаётся, пустое закроется потерей фокуса
                  if (event.key !== 'Enter') return;
                  event.preventDefault();
                  event.currentTarget.blur();
                }}
                placeholder={uiText('Найти шаблон')}
                aria-label={uiText('Найти шаблон')}
                autoComplete="off"
                enterKeyHint="search"
              />
              <button
                type="button"
                className="sx"
                tabIndex={searchShown ? 0 : -1}
                onClick={() => patch({ searchOpen: false, q: '' })}
                aria-label={uiText('Закрыть поиск')}
              >
                <StudioIcon kind="close" />
              </button>
            </div>
            <button
              type="button"
              className="sorticon st-lens"
              onClick={() => patch({ searchOpen: true })}
              aria-label={uiText('Поиск')}
              aria-controls={searchId}
              aria-expanded={searchShown}
              tabIndex={searchShown ? -1 : 0}
            >
              <StudioIcon kind="search" />
            </button>
            <button
              type="button"
              /* ⇅ залита, когда порядок не по умолчанию; по умолчанию у всех страниц — «Популярные» */
              className={`sorticon${sortOn || filterGroups.some(group => group.multi && group.value.length) ? ' on' : ''}`}
              aria-label={uiText('Сортировка')}
              aria-haspopup="dialog"
              aria-expanded={sortOpen}
              onClick={() => setSortOpen(true)}
            >
              <StudioIcon kind="sort" />
            </button>
            <FilterSheet
              id="stFilters"
              open={sortOpen}
              groups={filterGroups}
              texts={{ title: uiText('Сортировка и фильтры'), close: uiText('Закрыть'), reset: uiText('Сбросить'), done: uiText('Готово') }}
              onChange={filterChange}
              onClose={() => setSortOpen(false)}
            />
          </div>
        )}
        {state.view === 'tools' && (
          <StudioSegments items={sectionItems} value={state.section} onChange={section => patch({ section })} />
        )}
        {/* Каталог и страница-хозяин — одна строка вкладок прямо в шапке, как у «Всех инструментов» (вторая строка — в меню ⇅).
            У страницы-хозяина («Вы в кино») строку отдаёт сервер в top_tabs */}
        {state.view === 'catalog' || (isCollection && !!topTabs.length) ? (
          !!topTabs.length && (
            <StudioSegments
              items={topTabs.map(item => [text(item.id), text(item.label) || title(item)])}
              gold={goldTabs}
              value={isCollection ? state.top : state.tab}
              onChange={value =>
                patch(
                  isCollection
                    ? { top: value, cast: keepCast(value), format: 'all' }
                    : /* формат и состав остаются, если они есть у новой вкладки (subFit: вид — tabFit сразу,
                         неактивный пункт — fit по ответу). У «Подборок» их нет */
                      { tab: value, ...tabFit(value) },
                )
              }
            />
          )
        ) : isCollection && !list.ready ? (
          <StudioSegmentsSkeleton />
        ) : (
          isCollection &&
          !!subRow.length && (
            <StudioSegments
              className="st-tlseg"
              items={subRow.map(item => [text(item.id), text(item.label)])}
              value={castRow ? state.cast : state.format}
              disabled={serverDisabled}
              counts={serverDisabled ? undefined : castRow ? list.cast_counts : list.format_counts}
              onChange={value => patch(castRow ? { cast: value } : { format: value })}
            />
          )
        )}
      </Sticky>
      {isHome && (
        <>
          {/* Блок выдачи поиска главной (srBoxHTML): пустая сцена, строки группами, слой копий */}
          <div ref={searchBox} className="st-sr" hidden={!results}>
            <div className="st-sre">{results && (listFailed ? failure : zeroReady && <StudioZero {...zeroScene()} />)}</div>
            <div className="st-srl">
              {results &&
                searchGroups.map(group =>
                  group.items.length ? (
                    <Fragment key={group.key}>
                      <div className="st-h st-trh" data-k={`h:${group.key}`}>
                        <b>{uiText(group.label)}</b>
                        <span className="st-cnt">{group.items.length}</span>
                      </div>
                      {group.group === 'tool' ? (
                        <StudioToolRows items={group.items} onAction={onAction} />
                      ) : (
                        <div className="st-grp">{group.items.map(searchRow)}</div>
                      )}
                    </Fragment>
                  ) : null,
                )}
            </div>
            {ghostLayer}
          </div>
          <div ref={homeBody} className="st-body" hidden={results}>
            <StudioHome
              appTotal={appSummary.data?.total}
              home={home}
              artwork={artwork.data ?? undefined}
              artPending={artwork.pending}
              tools={tools.data?.items}
              state={state}
              /* автопрокрутка стоит под листом, экраном создания и выдачей поиска (stopAuto эталона: openPre, openApp, hqPaint) */
              paused={heroPaused || results}
              onChange={patch}
              onOpen={onOpen}
              onAction={onAction}
              onView={onView}
            />
          </div>
        </>
      )}
      {state.view === 'tools' && (
        <>
          <StudioStatus loading={!tools.data && !tools.error} error={tools.error} retry={tools.reload} />
          {/* Сцена пустой выдачи живёт в своём держателе .st-sre, список — в .st-tll (toolsPageHTML) */}
          <div className="st-sre">{!toolItems.length && tools.data && !tools.error && <StudioZero {...zeroScene()} />}</div>
          <StudioToolRows
            items={toolItems}
            onAction={onAction}
            /* подзаголовки — только во «Все» (tlHTML); без строк групп у эталона нет вовсе */
            grouped={state.section === 'all' || !toolItems.length}
          />
          {ghostLayer}
        </>
      )}
      {state.view === 'catalog' && (
        <>
          <StudioStatus error={catalog.error} retry={catalog.reload} />
          {!list.ready && !cardItems.length && !listFailed && <StudioCardsSkeleton />}
          {/* Пустая сцена каталога — в держателе .st-lbl: непустой он прячет ленту и строку догрузки */}
          <div className="st-lbl">{listFailed ? failure : zeroReady && <StudioZero {...zeroScene()} />}</div>
          <StudioCards items={cardItems} onOpen={onOpen} grid={hidesSubRow} />
          {/* Строка догрузки — div, у конца списка класс end; у ровной сетки «Подборок» она пустая */}
          <div ref={loadMore} className={`st-ldm${list.next && !moreFailed ? '' : ' end'}`}>
            {moreFailed ? (
              <span role="alert">
                {uiText('Не удалось загрузить студию.')}{' '}
                <button type="button" onClick={list.reload}>
                  {uiText('Обновить')}
                </button>
              </span>
            ) : hidesSubRow ? (
              ''
            ) : list.next ? (
              uiText('Загружаем ещё')
            ) : cardItems.length ? (
              uiText('Это все шаблоны')
            ) : (
              ''
            )}
          </div>
          {ghostLayer}
        </>
      )}
      {isCollection && (
        <>
          {/* Страница встаёт одним шагом: документ и первая страница пришли вместе; до первой страницы — скелетон плиток, как у «Озвучки» (владелец 02.11.2026), а не строка «Загружаем…» */}
          {!list.ready && <StudioCardsSkeleton />}
          <div className="st-sre">{list.ready && (listFailed ? failure : zeroReady && <StudioZero {...zeroScene()} />)}</div>
          <StudioCards items={cardItems} onOpen={onOpen} grid={hidesSubRow} />
          {ghostLayer}
        </>
      )}
      {/* точка снимка смены выдачи — после списка: замер после смены видит уже разложенную кладку */}
      {transition}
    </>
  );
}
